import React, { createContext, useContext, useState, useEffect } from 'react';
import { SUPPORT_EMAIL } from '@/config/constants';

type Language = 'nl' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  nl: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.vacancies': 'Vacatures',
    'nav.analytics': 'Analytics',
    'nav.companies': 'Bedrijven',
    'nav.users': 'Gebruikers',
    'nav.settings': 'Instellingen',
    'nav.logout': 'Uitloggen',
    'nav.login': 'Inloggen',
    'nav.requestQuote': 'Offerte aanvragen',
    
    // Hero Section
    'hero.title': 'Versnel je',
    'hero.titleHighlight': 'recruitmentproces',
    'hero.description': 'Werk samen vanuit één platform met realtime inzicht in klanten, vacatures en resultaten. Minder overleg, meer plaatsingen.',
    'hero.usp1': 'Realtime synchronisatie',
    'hero.usp2': 'Geografisch overzicht',
    'hero.usp3': 'Direct inzetbaar',
    'hero.cta': 'Versnel nu jouw recruitmentproces',
    
    // Map Section
    'map.badge': 'Realtime inzicht',
    'map.title': 'Krijg direct inzicht in waar je klanten en vacatures actief zijn, en ontdek waar de',
    'map.titleHighlight': 'grootste kansen',
    'map.titleEnd': 'liggen',
    'map.description': 'Interactieve kaart met realtime vacaturedata per regio.',
    'map.subdescription': 'Bekijk waar jij het verschil kunt maken',
    
    // Vacature Section
    'vacature.badge': 'Realtime inzicht',
    'vacature.title': 'Realtime inzicht –',
    'vacature.titleHighlight': 'alles in één overzicht',
    'vacature.description': 'Zie in één oogopslag waar je klanten en vacatures zitten en waar je team actief is. Recruiters hebben alle informatie direct in het systeem, zodat onnodige meetings met accountmanagers overbodig zijn. Interactieve realtime kaart per regio houdt alles overzichtelijk.',
    'vacature.subdescription': 'Bekijk je kansen in realtime',
    
    // Dashboard Section
    'dashboard.badge': 'Teamleider dashboard',
    'dashboard.title': 'Teamleider dashboard –',
    'dashboard.titleHighlight': 'stuur je team met inzicht',
    'dashboard.description': 'Zie precies waar je team actief is, waar de kansen liggen en waar je moet ingrijpen. Alle informatie direct beschikbaar, zodat je tijd bespaart en resultaten verbetert.',
    'dashboard.subdescription': 'Bekijk je team in realtime',
    
    // Stats
    'stats.vacancies': 'Actieve vacatures gesynchroniseerd',
    'stats.bureaus': 'Bureaus die sneller plaatsen',
    'stats.overhead': 'Minder overhead meetings',
    'stats.sync': 'Sales & recruiters in sync',
    
    // Benefits
    'benefits.badge': 'Waarom Khaylani?',
    'benefits.title': 'Sneller schakelen =',
    'benefits.titleHighlight': 'sneller plaatsen',
    'benefits.description': 'Minder vergaderingen, minder mails, minder gedoe - meer transparantie tussen recruiters, sales én management.',
    'benefits.1.title': 'Iedereen weet exact wat er speelt',
    'benefits.1.description': 'Accountmanager heeft een nieuwe aanvraag? Recruiter ziet het direct in het systeem. Van functie-eisen tot details over het bedrijf: de recruiter weet precies wat hij moet zoeken. Geen onnodige meetings meer tussen accountmanagers en recruiters over vacatures.',
    'benefits.2.title': 'Recruiters zien live wat sales binnenhaalt',
    'benefits.2.description': 'Nieuwe klant? Recruiter weet het meteen. Nieuwe vacature bij een bedrijf? Recruiter weet direct tot in detail wat hij of zij moet zoeken.',
    'benefits.2.subdescription': 'Maak het visueel duidelijk waar je actief bent en toon betrokkenheid. Recruiters weten exact waar vacatures openstaan en kunnen tijdens een intake concrete voorstellen doen. Zo ontstaat er vertrouwen en transparantie in de samenwerking met klanten.',
    'benefits.3.title': 'Teamleiders weten precies hoe en waar ze hun team moeten bijsturen',
    'benefits.3.description': 'Zie in één oogopslag waar je team actief is en waar de kansen liggen, zodat je doelgericht kunt bijsturen waar dat nodig is.',
    'benefits.4.title': 'Minder meetings, meer doen',
    'benefits.4.description': 'Elk uur dat een recruiter en accountmanager minder in overleg zitten, is een uur meer om te focussen op klanten en openstaande vacatures. Tijd is kostbaar - en Khaylani bespaart tijd, zodat je kunt focussen op wat écht belangrijk is.',
    
    // FAQ
    'faq.badge': 'FAQ',
    'faq.title': 'Veelgestelde vragen',
    'faq.description': 'Alles wat je moet weten over Khaylani',
    'faq.1.question': 'Wat is Khaylani?',
    'faq.1.answer': 'Khaylani is een recruitment dashboard voor detacheringsbureaus dat realtime inzicht biedt in klanten, vacatures en teamactiviteiten. Het centraliseert alle informatie op één plek, zodat je team altijd overzicht heeft.',
    'faq.2.question': 'Hoe snel kan ik starten met Khaylani?',
    'faq.2.answer': 'Na je offerteaanvraag ontvang je binnen 24 uur een persoonlijk voorstel. Zodra je akkoord geeft, plannen we een onboarding-meeting op maat in, zodat je snel en efficiënt live kunt gaan.',
    'faq.cta.description': 'Start vandaag nog met realtime inzicht in je recruitmentproces.',
    'faq.cta.button': 'Vraag een offerte aan',
    
    // Contact Form
    'contact.badge': 'Snelle reactie',
    'contact.title': 'Start vandaag nog met Khaylani',
    'contact.description': 'Binnen 24 uur ontvang je een voorstel op maat',
    'contact.form.name': 'Naam *',
    'contact.form.namePlaceholder': 'Je volledige naam',
    'contact.form.company': 'Bedrijfsnaam *',
    'contact.form.companyPlaceholder': 'Naam van je bedrijf',
    'contact.form.email': 'E-mailadres *',
    'contact.form.emailPlaceholder': 'je@bedrijf.nl',
    'contact.form.employees': 'Hoeveel gebruikers krijgen toegang tot Khaylani? *',
    'contact.form.employeesPlaceholder': 'Bijvoorbeeld: 5 recruiters, 2 accountmanagers, 1 teamleider',
    'contact.form.message': 'Eventuele vragen of opmerkingen *',
    'contact.form.messagePlaceholder': 'Vertel ons meer over je situatie...',
    
    // Auth & Password Reset
    'auth.forgotPassword': 'Wachtwoord vergeten?',
    'auth.backToLogin': 'Terug naar inloggen',
    'auth.resetPassword': 'Wachtwoord resetten',
    'auth.resetPasswordTitle': 'Wachtwoord vergeten',
    'auth.resetPasswordDescription': 'Voer je e-mailadres in om een resetlink te ontvangen',
    'auth.sendResetLink': 'Verstuur resetlink',
    'auth.resetEmailSent': 'Als dit e-mailadres bekend is, ontvang je binnen enkele minuten een e-mail met instructies.',
    'auth.newPassword': 'Nieuw wachtwoord',
    'auth.confirmPassword': 'Bevestig wachtwoord',
    'auth.updatePassword': 'Wachtwoord wijzigen',
    'auth.passwordUpdated': 'Je wachtwoord is succesvol gewijzigd.',
    'auth.passwordMismatch': 'Wachtwoorden komen niet overeen',
    'auth.invalidToken': 'Ongeldige of verlopen resetlink',
    'auth.tokenExpired': 'Link is verlopen',
    'auth.passwordTooShort': 'Wachtwoord moet minimaal 6 tekens zijn',
    'auth.welcome': 'Welkom terug',
    'auth.subtitle': 'Log in om verder te gaan',
    'auth.terms': 'Door in te loggen ga je akkoord met onze voorwaarden',
    'auth.branding.tagline': 'Het complete recruitmentplatform voor detacheringsbureaus',
    'auth.feature.companies': 'Klantbeheer',
    'auth.feature.companies.desc': 'Alle klantinformatie op één plek',
    'auth.feature.vacancies': 'Vacaturebeheer',
    'auth.feature.vacancies.desc': 'Realtime inzicht in alle openstaande vacatures',
    'auth.feature.analytics': 'Analytics',
    'auth.feature.analytics.desc': 'Diepgaande inzichten in je performance',
    'auth.feature.map': 'Kaartweergave',
    'auth.feature.map.desc': 'Geografisch overzicht van al je activiteiten',
    'contact.form.submit': 'Vraag een gratis offerte aan',
    'contact.form.submitting': 'Verzenden...',
    'contact.form.guarantee1': '100% vrijblijvend',
    'contact.form.guarantee2': 'Reactie binnen 24 uur',
    'contact.toast.success.title': 'Offerte aangevraagd! ✅',
    'contact.toast.success.description': 'Binnen 24 uur nemen we contact met je op met een persoonlijk voorstel.',
    'contact.toast.error.title': 'Formulier bevat fouten',
    'contact.toast.error.description': 'Controleer de velden en probeer opnieuw.',
    'contact.toast.error.general.title': 'Er ging iets mis',
    'contact.toast.error.general.description': `Probeer het later opnieuw of mail ons op ${SUPPORT_EMAIL}`,
    
    // Common
    'common.loading': 'Laden...',
    'common.save': 'Opslaan',
    'common.cancel': 'Annuleren',
    'common.delete': 'Verwijderen',
    'common.edit': 'Bewerken',
    'common.add': 'Toevoegen',
    'common.search': 'Zoeken...',
    'common.filter': 'Filteren',
    'common.export': 'Exporteren',
    'common.import': 'Importeren',
    'common.status': 'Status',
    'common.actions': 'Acties',
    'common.details': 'Details',
    'common.close': 'Sluiten',
  },
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.vacancies': 'Vacancies',
    'nav.analytics': 'Analytics',
    'nav.companies': 'Companies',
    'nav.users': 'Users',
    'nav.settings': 'Settings',
    'nav.logout': 'Logout',
    'nav.login': 'Login',
    'nav.requestQuote': 'Request Quote',
    
    // Hero Section
    'hero.title': 'Accelerate your',
    'hero.titleHighlight': 'recruitment process',
    'hero.description': 'Work together from one platform with real-time insight into clients, vacancies and results. Less meetings, more placements.',
    'hero.usp1': 'Real-time synchronization',
    'hero.usp2': 'Geographic overview',
    'hero.usp3': 'Ready to use',
    'hero.cta': 'Accelerate your recruitment process now',
    
    // Map Section
    'map.badge': 'Real-time Insights',
    'map.title': 'Get immediate insight into where your clients and vacancies are active, and discover where the',
    'map.titleHighlight': 'biggest opportunities',
    'map.titleEnd': 'lie',
    'map.description': 'Interactive map with real-time vacancy data per region.',
    'map.subdescription': 'See where you can make a difference',
    
    // Vacature Section
    'vacature.badge': 'Real-time Insights',
    'vacature.title': 'Real-time insights –',
    'vacature.titleHighlight': 'everything in one overview',
    'vacature.description': 'See at a glance where your clients and vacancies are and where your team is active. Recruiters have all information directly in the system, making unnecessary meetings with account managers obsolete. Interactive real-time map per region keeps everything clear.',
    'vacature.subdescription': 'View your opportunities in real-time',
    
    // Dashboard Section
    'dashboard.badge': 'Team Leader dashboard',
    'dashboard.title': 'Team Leader dashboard –',
    'dashboard.titleHighlight': 'manage your team with insights',
    'dashboard.description': 'See exactly where your team is active, where opportunities are, and where you need to intervene. All information directly available, so you save time and improve results.',
    'dashboard.subdescription': 'View your team in real-time',
    
    // Stats
    'stats.vacancies': 'Active vacancies synchronized',
    'stats.bureaus': 'Agencies placing faster',
    'stats.overhead': 'Less overhead meetings',
    'stats.sync': 'Sales & recruiters in sync',
    
    // Benefits
    'benefits.badge': 'Why Khaylani?',
    'benefits.title': 'Faster switching =',
    'benefits.titleHighlight': 'faster placements',
    'benefits.description': 'Fewer meetings, fewer emails, less hassle - more transparency between recruiters, sales and management.',
    'benefits.1.title': 'Everyone knows exactly what\'s happening',
    'benefits.1.description': 'Account manager has a new request? Recruiter sees it directly in the system. From job requirements to company details: the recruiter knows exactly what to search for. No more unnecessary meetings between account managers and recruiters about vacancies.',
    'benefits.2.title': 'Recruiters see live what sales brings in',
    'benefits.2.description': 'New client? Recruiter knows immediately. New vacancy at a company? Recruiter knows directly in detail what he or she needs to search for.',
    'benefits.2.subdescription': 'Make it visually clear where you are active and show commitment. Recruiters know exactly where vacancies are open and can make concrete proposals during an intake. This creates trust and transparency in collaboration with clients.',
    'benefits.3.title': 'Team leaders know exactly how and where to guide their team',
    'benefits.3.description': 'See at a glance where your team is active and where opportunities are, so you can make targeted adjustments where needed.',
    'benefits.4.title': 'Less meetings, more doing',
    'benefits.4.description': 'Every hour that a recruiter and account manager spend less in meetings is an hour more to focus on clients and open vacancies. Time is precious - and Khaylani saves time, so you can focus on what really matters.',
    
    // FAQ
    'faq.badge': 'FAQ',
    'faq.title': 'Frequently Asked Questions',
    'faq.description': 'Everything you need to know about Khaylani',
    'faq.1.question': 'What is Khaylani?',
    'faq.1.answer': 'Khaylani is a recruitment dashboard for staffing agencies that provides real-time insight into clients, vacancies and team activities. It centralizes all information in one place, so your team always has an overview.',
    'faq.2.question': 'How quickly can I start with Khaylani?',
    'faq.2.answer': 'After your quote request, you will receive a personal proposal within 24 hours. Once you agree, we schedule a customized onboarding meeting so you can go live quickly and efficiently.',
    'faq.cta.description': 'Start today with real-time insights into your recruitment process.',
    'faq.cta.button': 'Request a quote',
    
    // Contact Form
    'contact.badge': 'Fast Response',
    'contact.title': 'Start with Khaylani today',
    'contact.description': 'Receive a customized proposal within 24 hours',
    'contact.form.name': 'Name *',
    'contact.form.namePlaceholder': 'Your full name',
    'contact.form.company': 'Company name *',
    'contact.form.companyPlaceholder': 'Your company name',
    'contact.form.email': 'Email address *',
    'contact.form.emailPlaceholder': 'you@company.com',
    'contact.form.employees': 'How many users will get access to Khaylani? *',
    'contact.form.employeesPlaceholder': 'For example: 5 recruiters, 2 account managers, 1 team leader',
    'contact.form.message': 'Questions or comments *',
    'contact.form.messagePlaceholder': 'Tell us more about your situation...',
    
    // Auth & Password Reset
    'auth.forgotPassword': 'Forgot password?',
    'auth.backToLogin': 'Back to login',
    'auth.resetPassword': 'Reset password',
    'auth.resetPasswordTitle': 'Forgot password',
    'auth.resetPasswordDescription': 'Enter your email address to receive a reset link',
    'auth.sendResetLink': 'Send reset link',
    'auth.resetEmailSent': 'If this email address is registered, you will receive instructions within a few minutes.',
    'auth.newPassword': 'New password',
    'auth.confirmPassword': 'Confirm password',
    'auth.updatePassword': 'Update password',
    'auth.passwordUpdated': 'Your password has been successfully changed.',
    'auth.passwordMismatch': 'Passwords do not match',
    'auth.invalidToken': 'Invalid or expired reset link',
    'auth.tokenExpired': 'Link has expired',
    'auth.passwordTooShort': 'Password must be at least 6 characters',
    'auth.welcome': 'Welcome back',
    'auth.subtitle': 'Sign in to continue',
    'auth.terms': 'By signing in you agree to our terms and conditions',
    'auth.branding.tagline': 'The complete recruitment platform for staffing agencies',
    'auth.feature.companies': 'Client management',
    'auth.feature.companies.desc': 'All client information in one place',
    'auth.feature.vacancies': 'Vacancy management',
    'auth.feature.vacancies.desc': 'Real-time insight into all open vacancies',
    'auth.feature.analytics': 'Analytics',
    'auth.feature.analytics.desc': 'Deep insights into your performance',
    'auth.feature.map': 'Map view',
    'auth.feature.map.desc': 'Geographic overview of all your activities',
    'contact.form.submit': 'Request a free quote',
    'contact.form.submitting': 'Sending...',
    'contact.form.guarantee1': '100% non-binding',
    'contact.form.guarantee2': 'Response within 24h',
    'contact.toast.success.title': 'Quote requested! ✅',
    'contact.toast.success.description': 'We will contact you within 24 hours with a personal proposal.',
    'contact.toast.error.title': 'Form contains errors',
    'contact.toast.error.description': 'Check the fields and try again.',
    'contact.toast.error.general.title': 'Something went wrong',
    'contact.toast.error.general.description': `Please try again later or email us at ${SUPPORT_EMAIL}`,
    
    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search...',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.status': 'Status',
    'common.actions': 'Actions',
    'common.details': 'Details',
    'common.close': 'Close',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'nl';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['nl']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
